import { Spin } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import styles from "./LineChartCard.module.css";

export interface LineConfig {
  key: string;
  label: string;
  color: string;
}

export interface LineChartCardProps {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  lines: LineConfig[];
  yLabel?: string;
  loading?: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
}

const LineChartCard = ({
  title,
  data,
  xKey,
  lines,
  yLabel,
  loading = false,
  error = null,
  headerExtra,
}: LineChartCardProps) => {
  return (
    <div className={styles.card} data-chart-card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <h2 className={styles.title} style={{ margin: 0 }}>{title}</h2>
        {headerExtra}
      </div>

      <div className={styles.chartArea}>
        {loading && (
          <div className={styles.centered}>
            <Spin size="small" /> Carregant dades…
          </div>
        )}
        {!loading && error && (
          <div className={styles.centered}>
            <p className={styles.errorMessage}>{error}</p>
          </div>
        )}
        {!loading && !error && data.length === 0 && (
          <div className={styles.centered}>
            <p className={styles.emptyMessage}>Sense dades disponibles</p>
          </div>
        )}
        {!loading && !error && data.length > 0 && (
          <ResponsiveContainer width="100%" height="100%" minHeight={0}>
            <LineChart
              data={data}
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF3" />
              <XAxis
                dataKey={xKey}
                tick={{ fontSize: 12, fill: "#717C8B" }}
                tickLine={false}
                axisLine={{ stroke: "#E8EDF3" }}
              />
              <YAxis
                label={
                  yLabel
                    ? {
                        value: yLabel,
                        angle: -90,
                        position: "insideLeft",
                        style: { fontSize: 12, fill: "#717C8B" },
                        offset: 8,
                      }
                    : undefined
                }
                tick={{ fontSize: 12, fill: "#717C8B" }}
                tickLine={false}
                axisLine={false}
                width={60}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #E8EDF3",
                  fontSize: 13,
                }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              />
              {lines.map(({ key, label, color }) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={label}
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default LineChartCard;
